/**
 * Slice LV-02 · leviSpeech — thin wrapper around window.speechSynthesis
 * with default voice selection, TTS-priming for iOS user-gesture
 * requirement, stop-speaking, ducking, and a persisted voice-enabled
 * preference.
 *
 * Live-runtime failure investigation (PO QA fix):
 * The most common cause of "text arrives, audio doesn't" on Levi is
 * iOS Safari's user-gesture policy: the FIRST speechSynthesis.speak()
 * must fire inside a synchronous user gesture (touchend/click). Our
 * flow instead calls speak() AFTER `await api.post(...)`, which is
 * outside the gesture stack and iOS Safari silently drops the
 * utterance. We fix this with a silent-primer utterance triggered
 * INSIDE the user gesture (mic tap, test button, typed submit,
 * confirmation button) — it unlocks the audio session for the tab so
 * subsequent async speaks are honored.
 *
 * Diagnostics: every material event is pushed to a bounded ring
 * buffer accessible via getDiag()/window.__leviDiag. The buffer is
 * always kept in memory but is only surfaced in production copy when
 * the URL contains ?leviDiag=1 (or NODE_ENV !== 'production').
 */
const PREF_KEY = "jc:levi:tts-enabled";

// ---------- Diagnostic ring buffer ----------
const DIAG_LIMIT = 200;
const _diagBuf = [];
const _diagEnabled = () => {
  try {
    // PO 2026-02 · Bounded Preview-only QA instrumentation flag.
    // Production `.env` never sets this; Preview `.env` opts in so
    // manual QA (Gabe's Roofing acceptance) sees the voice diag panel
    // without ?leviDiag=1 / localStorage / DevTools.
    if (String(process.env.REACT_APP_LEVI_VOICE_DIAG_ENABLED || "")
          .toLowerCase() === "true") {
      return true;
    }
    if (typeof window !== "undefined") {
      if (window.location?.search?.includes("leviDiag=1")) return true;
      try { if (localStorage.getItem("jc:levi:diag") === "1") return true; } catch { /* noop */ }
    }
  } catch { /* noop */ }
  return false;
};
// Public gate for contractor-facing UI. LV-02 lock scope hides the
// diagnostic panel from the production experience; it stays reachable
// behind the internal developer flag (?leviDiag=1 or the sticky
// localStorage key jc:levi:diag=1). DEV/preview builds also hide the
// panel unless one of those explicit developer flags is set.
export const isDiagEnabled = _diagEnabled;
// If the URL carries ?leviDiag=1 at first module load, persist it so
// SPA navigation to routes without the query still surfaces the panel
// and console logs. ?leviDiag=0 clears it.
try {
  if (typeof window !== "undefined" && window.location?.search) {
    if (window.location.search.includes("leviDiag=1")) {
      try { localStorage.setItem("jc:levi:diag", "1"); } catch { /* noop */ }
    } else if (window.location.search.includes("leviDiag=0")) {
      try { localStorage.removeItem("jc:levi:diag"); } catch { /* noop */ }
    }
  }
} catch { /* noop */ }
const _pushDiag = (evt, data) => {
  const entry = { ts: Date.now(), evt, ...(data || {}) };
  _diagBuf.push(entry);
  if (_diagBuf.length > DIAG_LIMIT) _diagBuf.shift();
  if (_diagEnabled()) {
    try { console.debug("[leviSpeech]", evt, data || ""); } catch { /* noop */ }
  }
  try {
    if (typeof window !== "undefined") window.__leviDiag = _diagBuf;
  } catch { /* noop */ }
};
export const getDiag = (n = 30) => _diagBuf.slice(-n);
// PO 2026-08-07 diagnostic bounded round · expose _pushDiag so
// sibling Voice modules (e.g. leviWakeLock) can emit lifecycle
// events into the SAME ring buffer read by "Copy Full Diagnostics".
// This is diagnostic-only and does not alter any speech, TTS, VAD,
// microphone, wake-lock, or conversation behavior.
export const pushDiag = _pushDiag;

// ---------- Preference (robust to legacy/malformed values) ----------
export const isTtsEnabled = () => {
  try {
    const v = localStorage.getItem(PREF_KEY);
    // Anything other than the literal string "0" is treated as
    // enabled — missing, null, undefined, "", "1", "true", legacy
    // garbage all default to ON so a first-time contractor always
    // hears Levi.
    return v !== "0";
  } catch {
    return true;
  }
};
export const setTtsEnabled = (on) => {
  try {
    localStorage.setItem(PREF_KEY, on ? "1" : "0");
    _pushDiag("pref:set", { on: !!on });
  } catch { /* noop */ }
};
export const readPrefRaw = () => {
  try { return localStorage.getItem(PREF_KEY); } catch { return null; }
};

// ---------- Curated voice picker (unchanged) ----------
// Curated preferred voices used by ``pickEnglishVoice`` below. Order
// matters — earlier entries win. The list is intentionally scoped to
// browser-TTS fallback voices; server-side Custom Levi Voice, once
// activated separately, remains authoritative. Extended 2026-08-06
// per PO Decision P-1 to recognize male voices shipped on current
// iOS and Android browser/system inventories. All prior macOS and
// Windows selections are preserved verbatim; new entries are additive.
const EN_US_MALE_NAMES = [
  // macOS (preserved from Slice LEVI-VOICE-01).
  "Alex", "Aaron", "Fred", "Nathan", "Tom", "Bruce",
  // Windows (preserved).
  "David", "Mark", "Guy",
  // Cross-platform Google voice-name aliases (preserved).
  "Google US English Male", "Google UK English Male",
  // Chromium en-us voice codes (preserved — apply to desktop Chrome
  // and Android Chrome equally).
  "en-us-x-iom", "en-us-x-tpc", "en-us-x-tpd", "en-us-x-tpf",
  // Additional Windows male names (preserved).
  "Christopher", "Eric", "Roger", "Steffan", "Brandon",
  // P-1 (PO 2026-08-06) · iOS Safari male voices. iOS 15+ ships
  // Aaron (already above), Daniel (en-GB male), and iOS 17.4+
  // ships Reed and Rocko. Rishi is an en-IN male voice available
  // in the "Voice for Levi" enhanced voices download. Junior and
  // Grandpa are older male-timbre iOS voices.
  "Daniel", "Reed", "Rocko", "Rishi", "Junior", "Grandpa",
  // P-1 · Android Chrome male voice codes (Google TTS engine).
  // ``en-us-x-tpc/tpd/tpf`` already above are the primary male
  // codes on Android; adding the "-local" and "-network" forms
  // observed on real devices for defensive matching.
  "en-us-x-tpc-local", "en-us-x-tpd-local", "en-us-x-tpf-local",
  "en-us-x-tpc-network", "en-us-x-tpd-network", "en-us-x-tpf-network",
  // P-1 · Edge (Chromium) additional male natural voices commonly
  // installed on Windows 11 language packs.
  "Andrew", "Brian", "Christopher (Enhanced)", "Guy (Enhanced)",
  "Aria", // NOTE: female — intentionally NOT in this list; kept as
          // comment marker to prevent accidental future additions.
].filter((n) => n !== "Aria");
const EN_EXCLUDE = new Set([
  "Bad News", "Bahh", "Bells", "Boing", "Bubbles", "Cellos", "Deranged",
  "Good News", "Hysterical", "Pipe Organ", "Trinoids", "Whisper", "Zarvox",
  "Albert", "Junior", "Ralph", "Kathy",
]);
const ES_PREFERRED_NAMES = [
  "Jorge", "Diego", "Juan", "Paulina", "Monica", "Marisol",
  "Google español", "Google español de Estados Unidos",
  "Microsoft Sabina", "Microsoft Raul", "Microsoft Pablo",
  "Microsoft Jorge", "Microsoft Dalia", "Microsoft Alonso",
];
const _byName = (voices, wanted) => {
  const target = wanted.toLowerCase();
  return voices.find((v) => (v.name || "").toLowerCase().includes(target));
};
const pickEnglishVoice = (voices) => {
  const enVoices = voices.filter(
    (v) => (v.lang || "").toLowerCase().startsWith("en") && !EN_EXCLUDE.has(v.name)
  );
  if (!enVoices.length) return null;
  for (const name of EN_US_MALE_NAMES) {
    const hit = enVoices.find(
      (v) => (v.lang || "").toLowerCase() === "en-us" &&
             (v.name || "").toLowerCase().includes(name.toLowerCase())
    );
    if (hit) return hit;
  }
  return enVoices.find((v) => (v.lang || "").toLowerCase() === "en-us" && v.localService)
      || enVoices.find((v) => (v.lang || "").toLowerCase() === "en-us")
      || enVoices.find((v) => v.localService)
      || enVoices[0];
};
const pickSpanishVoice = (voices) => {
  const esVoices = voices.filter((v) => (v.lang || "").toLowerCase().startsWith("es"));
  if (!esVoices.length) return null;
  for (const name of ES_PREFERRED_NAMES) {
    const hit = _byName(esVoices, name);
    if (hit) return hit;
  }
  for (const lang of ["es-us", "es-419", "es-mx", "es-la", "es-co", "es-ar"]) {
    const hit = esVoices.find((v) => (v.lang || "").toLowerCase() === lang);
    if (hit) return hit;
  }
  return esVoices.find((v) => v.localService) || esVoices[0];
};

// ---------- Async voice enumeration cache ----------
let _voicesCache = [];
const _refreshVoices = () => {
  try {
    _voicesCache = (window.speechSynthesis && window.speechSynthesis.getVoices()) || [];
  } catch { _voicesCache = []; }
};
if (typeof window !== "undefined" && window.speechSynthesis) {
  _refreshVoices();
  const onChange = () => { _refreshVoices(); _pushDiag("voiceschanged", { count: _voicesCache.length }); };
  try { window.speechSynthesis.addEventListener("voiceschanged", onChange); }
  catch { try { window.speechSynthesis.onvoiceschanged = onChange; } catch { /* noop */ } }
}
export const pickVoice = (lang) => {
  if (!_voicesCache.length) _refreshVoices();
  // Slice LEVI-VOICE-01 · consult the semantic preference model +
  // ranker first. Falls through to the legacy curated pickers if no
  // ranked voice is available (defensive fallback preserves the LV-02
  // behavior for tests and edge devices).
  let chosen = null;
  let source = "legacy_curated";
  try {
    // Dynamic requires break test-time module cycles; static imports
    // are fine here at runtime.
    // eslint-disable-next-line global-require
    const { readVoicePrefs, setPreferredVoiceUri, getPreferredVoiceUri } = require("./leviVoicePrefs");
    // eslint-disable-next-line global-require
    const { chooseVoice } = require("./leviVoiceRanking");
    const prefs = readVoicePrefs(lang || "en");
    // Try the cached preferred voice first, but only if it is still
    // available on the device.
    const cachedUri = getPreferredVoiceUri();
    if (cachedUri) {
      const cached = _voicesCache.find(
        (v) => (v.voiceURI && v.voiceURI === cachedUri) ||
               (v.name && v.name === cachedUri)
      );
      if (cached && (cached.lang || "").toLowerCase().startsWith(prefs.language)) {
        chosen = cached;
        source = "prefs_cached_uri";
      }
    }
    if (!chosen) {
      chosen = chooseVoice(_voicesCache, prefs);
      if (chosen) {
        source = "prefs_ranked";
        // Cache the chosen voice URI so subsequent speaks are stable
        // (avoids re-ranking churn when the voice list is unchanged).
        setPreferredVoiceUri(chosen.voiceURI || chosen.name || null);
      }
    }
  } catch (_) {
    chosen = null;
  }
  if (!chosen) {
    chosen = (lang === "es" ? pickSpanishVoice : pickEnglishVoice)(_voicesCache);
    source = "legacy_curated";
  }
  const final = chosen || _voicesCache[0] || null;
  _pushDiag("pickVoice", {
    lang,
    chosen: chosen ? { name: chosen.name, lang: chosen.lang, local: chosen.localService } : null,
    source,
    fallback_used: !!(final && !chosen),
    voices_available: _voicesCache.length,
  });
  return final;
};

// ---------- TTS priming (iOS user-gesture unlock) ----------
//
// iOS Safari requires the FIRST speechSynthesis.speak() call in a tab
// to occur synchronously within a user gesture. Because Levi speaks in
// response to an async server reply, the gesture is stale by the time
// we call speak(). This primer runs INSIDE the user-gesture stack (mic
// tap, keyboard submit, dev-only test button) and unlocks the audio
// session for the rest of the tab's lifetime. It is a silent
// zero-volume utterance so no audible artifact leaks.
let _primed = false;
export const primeTts = (label = "unspecified") => {
  if (typeof window === "undefined" || !window.speechSynthesis) {
    _pushDiag("prime:noop:no-synth"); return false;
  }
  if (_primed) { _pushDiag("prime:already", { label }); return true; }
  try {
    const p = new SpeechSynthesisUtterance(" ");
    p.volume = 0;
    p.rate = 1.0;
    p.pitch = 1.0;
    p.onstart = () => _pushDiag("prime:onstart");
    p.onend = () => _pushDiag("prime:onend");
    p.onerror = (e) => _pushDiag("prime:onerror", { code: e?.error });
    window.speechSynthesis.speak(p);
    _primed = true;
    _pushDiag("prime:invoked", { label, speaking: window.speechSynthesis.speaking, pending: window.speechSynthesis.pending });
    return true;
  } catch (err) {
    _pushDiag("prime:threw", { err: String(err), label });
    return false;
  }
};
export const isPrimed = () => _primed;

// ---------- C-2 · Speaking-state render bridge (rendering only) ----------
//
// Publish/subscribe channel so React consumers (LeviCommandConsole via
// useLeviCommand) can re-render the presence badge at real-time
// speech-start and speech-end boundaries. THIS IS A RENDERING-ONLY
// BRIDGE. It does NOT change WHEN Levi speaks, WHEN he stops, WHETHER
// the microphone re-arms, WHETHER a conversation continues, or WHETHER
// a conversation ends. `isSpeaking()` remains the authoritative source
// of truth for presence derivation; this bridge only guarantees that
// the React tree re-evaluates it at the correct moments.
//
// The publisher fires exclusively at existing lifecycle boundaries
// (browser TTS onstart / final onend / onerror; server audio onplay /
// onended / onpause; cancelActiveSpeech; stopSpeaking). It does not
// introduce any new state machine.
const _speakingSubs = new Set();
let _lastPublishedSpeaking = false;
const _publishSpeaking = (isNow) => {
  const b = !!isNow;
  if (b === _lastPublishedSpeaking) return;
  _lastPublishedSpeaking = b;
  _pushDiag("speaking:publish", { speaking: b });
  for (const cb of _speakingSubs) {
    try { cb(b); } catch { /* noop */ }
  }
};
export const subscribeSpeakingChange = (cb) => {
  if (typeof cb !== "function") return () => {};
  _speakingSubs.add(cb);
  // Fire once with the current state so subscribers hydrate at mount.
  try { cb(_lastPublishedSpeaking); } catch { /* noop */ }
  return () => { _speakingSubs.delete(cb); };
};

// ---------- C-1 · Chromium long-speech reliability helpers ----------
//
// Chromium (desktop + Chromium-based mobile) has a well-known bug
// where speechSynthesis.speak() silently stops firing audio events
// after ~15 seconds of a single utterance. Two mitigations are
// applied — both isolated to browser-TTS playback reliability and
// isolated to this module:
//
//   1. Long replies are split into sentence-boundary chunks and
//      queued sequentially through the existing speechSynthesis
//      queue. The caller-provided onEnd fires EXACTLY ONCE — when
//      the last chunk ends OR on any error / cancel / stale token
//      (identical contract to the pre-C-1 single-utterance path).
//
//   2. While a chunk is playing on a Chromium user-agent, a gentle
//      pause() / resume() keep-alive interval runs every 10 s. This
//      is a no-op on Safari and Firefox.
//
// Neither mitigation changes conversation state, microphone timing,
// end-conversation behavior, fallback selection, or the onEnd
// contract observed by useLeviCommand.js `_onSpokenEnd`.
const _CHUNK_MAX_CHARS = 180;
const _isChromiumLike = () => {
  try {
    if (typeof navigator === "undefined") return false;
    const ua = navigator.userAgent || "";
    // Chromium user-agents include "Chrome/". Firefox includes
    // "Firefox/" and does not need chunking. Safari includes
    // "Safari/" without "Chrome/" and does not need chunking.
    return /Chrome\//.test(ua) && !/Firefox\//.test(ua);
  } catch { return false; }
};
const _splitIntoChunks = (text) => {
  const clean = String(text || "").trim();
  if (clean.length <= _CHUNK_MAX_CHARS) return [clean];
  // Sentence-boundary split first (preserves EN/ES punctuation and
  // the Spanish inverted question / exclamation marks live inside
  // the sentence body — the regex only splits AFTER terminal marks).
  const sentences = clean.match(/[^.!?]+[.!?]+["')\]]*\s*|[^.!?]+$/g)
                     || [clean];
  const out = [];
  let buf = "";
  for (const s of sentences) {
    const piece = s.trim();
    if (!piece) continue;
    const candidate = buf ? `${buf} ${piece}` : piece;
    if (candidate.length <= _CHUNK_MAX_CHARS) {
      buf = candidate;
    } else {
      if (buf) out.push(buf);
      if (piece.length <= _CHUNK_MAX_CHARS) {
        buf = piece;
      } else {
        // Rare pathological single sentence exceeding the cap.
        // Hard-split on word boundaries so we never send a single
        // utterance longer than the cap.
        const words = piece.split(/\s+/);
        let inner = "";
        for (const w of words) {
          const c2 = inner ? `${inner} ${w}` : w;
          if (c2.length <= _CHUNK_MAX_CHARS) {
            inner = c2;
          } else {
            if (inner) out.push(inner);
            inner = w;
          }
        }
        buf = inner;
      }
    }
  }
  if (buf) out.push(buf);
  return out;
};

// ---------- Speak / stop ----------
let _currentUtter = null; // keep a stable reference to defeat GC
let _lastSpeakToken = 0;
// Phase 7-C · track the currently-playing server-TTS <audio> element
// and its onEnd binding so cancelActiveSpeech() can stop it safely
// even after multiple invocations. Idempotent: subsequent cancels
// on the same audio are no-ops.
let _currentServerAudio = null;
let _currentServerAudioOnEnd = null;
let _currentServerAudioCancelled = false;
// Cloudflare-origin defense (PO 2026-08-04 QA). Sticky flag: once
// the server TTS route has returned an infrastructure-level
// failure this tab session (503, timeout, network error), we
// short-circuit future calls straight to browser speechSynthesis
// so we don't keep round-tripping through the edge on a doomed
// endpoint. Cleared on reload.
let _serverTtsSessionUnavailable = false;

// Public helper for Package 7-C barge-in. Safe to call repeatedly.
// Stops browser TTS AND the server-TTS <audio> element (if any),
// and fires the caller-registered onEnd exactly once per utterance
// with the supplied reason.
export const cancelActiveSpeech = (reason = "cancelled") => {
  let acted = false;
  // Server-TTS side.
  const audio = _currentServerAudio;
  const audioOnEnd = _currentServerAudioOnEnd;
  if (audio && !_currentServerAudioCancelled) {
    _currentServerAudioCancelled = true;
    try { audio.pause(); } catch { /* noop */ }
    try { audio.currentTime = 0; } catch { /* noop */ }
    // Nulling src prevents further playback. Revoke handled by the
    // element's own onended in the normal path; on cancel we also
    // revoke defensively.
    try { audio.src = ""; } catch { /* noop */ }
    _pushDiag("cancelActiveSpeech:server_audio", { reason });
    try {
      if (typeof audioOnEnd === "function") {
        audioOnEnd({ reason });
      }
    } catch { /* noop */ }
    acted = true;
  }
  _currentServerAudio = null;
  _currentServerAudioOnEnd = null;
  // Browser TTS side.
  try {
    if (typeof window !== "undefined"
        && window.speechSynthesis
        && (window.speechSynthesis.speaking
            || window.speechSynthesis.pending)) {
      window.speechSynthesis.cancel();
      _pushDiag("cancelActiveSpeech:browser_tts", { reason });
      acted = true;
    }
  } catch { /* noop */ }
  // C-2 · render bridge: publish speaking=false when either channel
  // was actually torn down by the cancel. `isSpeaking()` is the
  // authoritative source and will re-evaluate correctly on the next
  // render triggered by this publish.
  if (acted) _publishSpeaking(false);
  return acted;
};

const _actuallySpeak = (text, lang, token, onEnd) => {
  // Phase 7-C · idempotent onEnd wrapper for browser TTS. Cancel
  // fires onerror; natural completion fires onend. Either path
  // must invoke the caller exactly once.
  //
  // C-1 (2026-08-06): the same wrapper now also fires exactly once
  // per CALL rather than once per utterance. When the reply is
  // chunked for Chromium long-speech reliability, only the LAST
  // chunk's onend fires _fireOnce("ended"); intermediate chunks
  // advance the queue without notifying the caller. Errors and
  // stale tokens still terminate the whole call once.
  let _onEndFired = false;
  const _fireOnce = (reason) => {
    if (_onEndFired) return;
    _onEndFired = true;
    try { if (typeof onEnd === "function") onEnd({ reason }); }
    catch { /* noop */ }
  };
  try {
    if (token !== _lastSpeakToken) {
      _pushDiag("speak:stale-token", { token, latest: _lastSpeakToken });
      _fireOnce("stale_token");
      return;
    }
    // Voice + rate + language tag are resolved ONCE for the entire
    // reply so pronunciation stays uniform across chunks (identical
    // to the pre-C-1 single-utterance behavior).
    const v = pickVoice(lang);
    // Slice LEVI-VOICE-01 · read speaking rate from the semantic
    // preference model. Fallback to 1.0 if the model is unavailable.
    let _rate = 1.0;
    try {
      // eslint-disable-next-line global-require
      const { getSpeakingRate } = require("./leviVoicePrefs");
      _rate = getSpeakingRate();
    } catch (_) { _rate = 1.0; }
    const langTag = (v && v.lang) ? v.lang : (lang === "es" ? "es-US" : "en-US");
    // C-1 · chunk long text on Chromium. Non-Chromium browsers take
    // the single-chunk path so their behavior is byte-identical to
    // pre-C-1. Empty / short text also single-chunks.
    const _needsChunking = _isChromiumLike() && String(text).trim().length > _CHUNK_MAX_CHARS;
    const chunks = _needsChunking ? _splitIntoChunks(text) : [String(text)];
    // C-1 · Chromium keep-alive. Every 10 s pause()+resume() to
    // counteract the ~15 s silent-stall bug. Idempotent; disposed
    // by _stopKeepAlive on any terminal path.
    let _keepAliveTimer = null;
    const _startKeepAlive = () => {
      if (_keepAliveTimer) return;
      if (!_isChromiumLike()) return;
      if (typeof window === "undefined" || !window.speechSynthesis) return;
      _keepAliveTimer = setInterval(() => {
        try {
          const ss = window.speechSynthesis;
          if (!ss) return;
          if (ss.speaking && !ss.paused) {
            ss.pause();
            ss.resume();
          }
        } catch { /* noop */ }
      }, 10000);
    };
    const _stopKeepAlive = () => {
      if (_keepAliveTimer) {
        try { clearInterval(_keepAliveTimer); } catch { /* noop */ }
        _keepAliveTimer = null;
      }
    };
    // Sequential chunk pump. Preserves the pre-C-1 diag surface for
    // the FIRST chunk (utterance:start, speak:invoked) and adds
    // per-chunk diag events for observability.
    let _idx = 0;
    const _speakNext = () => {
      if (_onEndFired) return;
      if (token !== _lastSpeakToken) {
        _pushDiag("speak:stale-mid-chunk", { token, latest: _lastSpeakToken, i: _idx });
        _stopKeepAlive();
        _publishSpeaking(false);
        _fireOnce("stale_mid_chunk");
        return;
      }
      if (_idx >= chunks.length) {
        _pushDiag("utterance:all-chunks-end", { count: chunks.length });
        _stopKeepAlive();
        _publishSpeaking(false);
        _fireOnce("ended");
        return;
      }
      const seg = chunks[_idx];
      const u = new SpeechSynthesisUtterance(String(seg));
      u.lang = langTag;
      if (v) u.voice = v;
      u.rate = _rate;
      u.pitch = 0.95;
      u.volume = 1.0;
      u.onstart = () => {
        if (_idx === 0) {
          _pushDiag("utterance:start", {
            voice: v?.name || "default", lang: u.lang, textLen: text.length,
            chunks: chunks.length,
            speaking: window.speechSynthesis.speaking,
          });
          _publishSpeaking(true);
        }
        _startKeepAlive();
      };
      u.onend = () => {
        _pushDiag("utterance:end", {
          voice: v?.name || "default",
          i: _idx, total: chunks.length,
        });
        _idx += 1;
        _speakNext();
      };
      u.onerror = (e) => {
        _pushDiag("utterance:error", {
          code: e?.error || "unknown", voice: v?.name || "default",
          i: _idx, total: chunks.length,
        });
        _stopKeepAlive();
        _publishSpeaking(false);
        _fireOnce("error");
      };
      _currentUtter = u; // stable ref, prevents GC before onend/onerror
      window.speechSynthesis.speak(u);
      if (_idx === 0) {
        _pushDiag("speak:invoked", {
          voice: v?.name || "default",
          lang: u.lang,
          textLen: text.length,
          chunks: chunks.length,
          primed: _primed,
          speaking: window.speechSynthesis.speaking,
          pending: window.speechSynthesis.pending,
          paused: window.speechSynthesis.paused,
          voice_available: _voicesCache.length,
        });
      }
    };
    _speakNext();
  } catch (err) {
    _pushDiag("speak:threw", { err: String(err) });
    _publishSpeaking(false);
    _fireOnce("threw");
  }
};

export const speak = (text, lang = "en", opts = {}) => {
  const onEnd = typeof opts.onEnd === "function" ? opts.onEnd : null;
  const _fireEnd = (reason) => {
    try { if (onEnd) onEnd({ reason }); } catch { /* noop */ }
  };
  if (!text || typeof window === "undefined" || !window.speechSynthesis) {
    _pushDiag("speak:noop", {
      textLen: (text || "").length,
      hasSynth: !!(typeof window !== "undefined" && window.speechSynthesis),
    });
    // Phase 7-B · fire onEnd even on no-op so the caller's auto-
    // listen path still runs when TTS is unavailable.
    _fireEnd("noop_no_synth_or_text");
    return;
  }
  if (!isTtsEnabled()) {
    _pushDiag("speak:disabled", { textLen: text.length });
    _fireEnd("noop_tts_disabled");
    return;
  }
  const token = ++_lastSpeakToken;
  const busy = window.speechSynthesis.speaking || window.speechSynthesis.pending;
  if (busy) {
    _pushDiag("cancel:before-speak", { reason: "duck", speaking: window.speechSynthesis.speaking, pending: window.speechSynthesis.pending });
    try { window.speechSynthesis.cancel(); } catch { /* noop */ }
    // iOS Safari race: after cancel() the queue isn't immediately
    // ready. A 60ms delay is enough to let the engine settle without
    // impacting perceived latency.
    setTimeout(() => _actuallySpeak(text, lang, token, onEnd), 60);
  } else {
    _actuallySpeak(text, lang, token, onEnd);
  }
};

export const stopSpeaking = (reason = "unspecified") => {
  try {
    _pushDiag("stopSpeaking", {
      reason,
      speaking: window.speechSynthesis?.speaking,
      pending: window.speechSynthesis?.pending,
    });
    window.speechSynthesis?.cancel?.();
  } catch { /* noop */ }
  _currentUtter = null;
  // C-2 · render bridge: publish speaking=false defensively. The
  // browser TTS `onerror` path also publishes false, but the cancel
  // may race the event loop and the presence badge should reflect
  // "not speaking" immediately.
  _publishSpeaking(false);
};

export const isSpeaking = () => {
  try {
    // Phase 7-C · isSpeaking must reflect BOTH channels so barge-in
    // detection arms correctly during server-TTS playback (not just
    // browser TTS).
    if (_currentServerAudio && !_currentServerAudioCancelled) {
      const a = _currentServerAudio;
      if (a && !a.paused && !a.ended) return true;
    }
    return !!window.speechSynthesis?.speaking;
  } catch { return false; }
};

// ---------- Shared Levi-reply entry point ----------
//
// Both the diagnostic "Test Levi Voice" button AND every normal Levi
// conversational reply (from LV-01 voice-intake, LV-02 command
// console, and any future mode) MUST call this function to produce
// spoken output. This guarantees a single canonical speech pipeline
// and gives the Product Owner a single diagnostic event
// ("normalReply:speakRequested") to trace when a normal reply fails to
// speak.
//
// Behavior:
//   - Emits `normalReply:speakRequested` (or `speakLeviReply:test`
//     when opts.source === "test") with reply length, locale,
//     tts_enabled, current mode/status, and any extra ctx fields.
//   - Honors the contractor's voice-off preference for normal replies.
//     The dev "test" source bypasses the preference so the PO can
//     always prove browser audio in isolation.
//   - Never speaks empty text.
//   - Delegates the actual playback to `speak()` — same code path as
//     the diagnostic Test button.
//
export const speakLeviReply = (text, locale = "en", opts = {}) => {
  const source = opts.source || "normal_reply";
  const enabled = isTtsEnabled();
  const eventName = source === "test" ? "speakLeviReply:test" : "normalReply:speakRequested";
  const _onEnd = typeof opts.onEnd === "function" ? opts.onEnd : null;
  const _fireEnd = (reason) => {
    try { if (_onEnd) _onEnd({ reason }); } catch { /* noop */ }
  };
  // P0 REPAIR-2 (PO 2026-08-09) · monotonic speech turn id so a
  // canceled utterance in the diag ring buffer can be attributed to
  // the specific speak-request that canceled it. Also mirrored to
  // window.__leviLastSpeechTurn for Copy Diagnostics correlation.
  const _turnId = (typeof window !== "undefined")
    ? ((window.__leviSpeechTurnCounter = (window.__leviSpeechTurnCounter || 0) + 1))
    : 1;
  try {
    if (typeof window !== "undefined") {
      window.__leviLastSpeechTurn = {
        id: _turnId,
        source, mode: opts.mode || "unknown",
        reply_len: (text || "").length,
        requested_at: Date.now(),
      };
    }
  } catch { /* noop */ }
  _pushDiag(eventName, {
    speech_turn_id: _turnId,
    reply_len: (text || "").length,
    locale,
    tts_enabled: enabled,
    source,
    mode: opts.mode || "unknown",
    status: opts.status || "unknown",
    is_listening: !!opts.isListening,
  });
  if (!text || !String(text).trim()) {
    _pushDiag("normalReply:blocked", { reason: "empty_text", source });
    _fireEnd("blocked_empty_text");
    return false;
  }
  if (opts.isListening) {
    _pushDiag("normalReply:blocked", { reason: "listening", source });
    _fireEnd("blocked_listening");
    return false;
  }
  if (!enabled && source !== "test") {
    _pushDiag("normalReply:blocked", { reason: "tts_disabled", source });
    _fireEnd("blocked_tts_disabled");
    return false;
  }
  speak(text, locale, { onEnd: _onEnd });
  return true;
};

// ---------- Phase 7-D · server-TTS branch ----------
// Fetches an MP3 blob from POST /api/levi/tts/reply and plays it via
// an HTMLAudioElement. Falls back to window.speechSynthesis on ANY
// failure (network, 5xx, 503 tts_unavailable, decode error, autoplay
// block). Fire-and-forget — never throws to the caller.
//
// Contract:
//   text          reply text as composed by the server (already
//                 style-transformed).
//   locale        "en" or "es".
//   opts.voice_id "professional" | "warm" — required for server path.
//                 When absent, the function falls back directly to
//                 speechSynthesis (equivalent to speakLeviReply).
//   opts.style    "brief" | "balanced" | "conversational".
//   opts.speed    "slow" | "normal" | "fast".
//   opts.readAloud When explicitly false, plays nothing (parity with
//                 the server-side `levi_read_aloud_enabled`
//                 preference).
export const playServerTtsForReply = (text, locale = "en", opts = {}) => {
  const enabled = isTtsEnabled();
  const onEnd = typeof opts.onEnd === "function" ? opts.onEnd : null;
  // Recorder-lifecycle report 2026-08-04 · optional side-channel
  // callback the caller can subscribe to for HONEST audio-drop
  // notices. Fires AT MOST ONCE when the server route fails and we
  // fall back to browser speechSynthesis. Reason strings mirror the
  // ``runFallback`` argument.
  const onFallback = typeof opts.onFallback === "function"
    ? opts.onFallback : null;
  // PO 2026-08-30 · P0 Voice · D1 (late-arm barge-in). Fires AT MOST
  // ONCE when the SERVER-TTS success path actually begins playing
  // through the HTMLAudioElement — i.e. when Chromium's WebRTC AEC
  // has a reference signal it can subtract from the mic. It is
  // deliberately NOT called on the browser-speechSynthesis fallback
  // route (runFallback → speak) because that route bypasses the
  // browser AEC reference on Windows/Edge and self-output can bleed
  // into the mic at levels indistinguishable from human voice.
  const onPlaybackStart = typeof opts.onPlaybackStart === "function"
    ? opts.onPlaybackStart : null;
  let _playbackStartFired = false;
  const _firePlaybackStart = () => {
    if (_playbackStartFired) return;
    _playbackStartFired = true;
    try { if (onPlaybackStart) onPlaybackStart({ source: "server_tts" }); }
    catch { /* noop */ }
  };
  let _fallbackFired = false;
  const _fireFallback = (reason) => {
    if (_fallbackFired) return;
    _fallbackFired = true;
    try { if (onFallback) onFallback({ reason }); }
    catch { /* noop */ }
  };
  // Phase 7-C · idempotent onEnd wrapper. cancelActiveSpeech and
  // the browser-queued 'ended' event can both race for the same
  // utterance; the wrapper guarantees the caller sees exactly one
  // fire per playback session.
  let _onEndFired = false;
  const _fireEnd = (reason) => {
    if (_onEndFired) return;
    _onEndFired = true;
    try { if (onEnd) onEnd({ reason }); } catch { /* noop */ }
  };
  // Cloudflare-origin defense (PO 2026-08-04 QA). Once the server
  // TTS route has returned an infrastructure-level failure this
  // tab session (503 tts_unavailable, timeout, or network error),
  // skip the round-trip entirely for subsequent replies and fall
  // straight to browser speechSynthesis. Reduces the surface area
  // for edge/proxy retries that can surface as
  // "origin returned invalid or incomplete response." Cleared on
  // reload.
  if (_serverTtsSessionUnavailable) {
    _fireFallback("cached_unavailable");
    try { speak(text, locale, { onEnd }); } catch { _fireEnd("fallback_threw"); }
    return true;
  }
  if (opts.readAloud === false) {
    _pushDiag("serverTts:blocked", { reason: "read_aloud_disabled" });
    // Phase 7-B · fire onEnd immediately so the caller's auto-listen
    // path still arms when TTS is intentionally silent (contractor
    // has read-aloud disabled). Without this, the loop stalls.
    _fireEnd("blocked_read_aloud_disabled");
    return false;
  }
  if (!text || !String(text).trim()) {
    _pushDiag("serverTts:blocked", { reason: "empty_text" });
    _fireEnd("blocked_empty_text");
    return false;
  }
  if (!enabled) {
    _pushDiag("serverTts:blocked", { reason: "tts_disabled" });
    _fireEnd("blocked_tts_disabled");
    return false;
  }
  if (!opts.voice_id) {
    return speakLeviReply(text, locale, opts);
  }
  const runFallback = (reason) => {
    _pushDiag("serverTts:fallback", { reason });
    // Recorder-lifecycle 2026-08-04 · notify caller (once) so it can
    // surface an honest inline notice about the audio drop. Never
    // implies the contractor's device is at fault.
    _fireFallback(reason);
    // Fallback path also needs to fire onEnd so auto-listen still
    // arms after the browser TTS completes.
    try { speak(text, locale, { onEnd }); } catch { _fireEnd("fallback_threw"); }
  };
  (async () => {
    try {
      const apiBase = (typeof process !== "undefined"
        && process.env
        && process.env.REACT_APP_BACKEND_URL) || "";
      let token = "";
      try { token = localStorage.getItem("jc_token") || ""; } catch { /* noop */ }
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 8000);
      const r = await fetch(`${apiBase}/api/levi/tts/reply`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          text: String(text),
          voice_id: opts.voice_id,
          language: locale || "en",
          style: opts.style || "balanced",
          speed: opts.speed || "normal",
        }),
        signal: controller.signal,
      });
      clearTimeout(timer);
      if (!r.ok) {
        runFallback(`http_${r.status}`);
        return;
      }
      const blob = await r.blob();
      if (!blob || blob.size < 100) {
        runFallback("empty_blob");
        return;
      }
      const url = URL.createObjectURL(blob);
      const audio = new Audio(url);
      // Phase 7-C · register this audio + its onEnd so
      // cancelActiveSpeech() can stop it. Cleared on any terminal
      // path (ended / error / cancel).
      _currentServerAudio = audio;
      _currentServerAudioOnEnd = onEnd;
      _currentServerAudioCancelled = false;
      const _clearAudioRef = () => {
        if (_currentServerAudio === audio) {
          _currentServerAudio = null;
          _currentServerAudioOnEnd = null;
        }
      };
      audio.onended = () => {
        _clearAudioRef();
        try { URL.revokeObjectURL(url); } catch { /* noop */ }
        // C-2 · render bridge.
        _publishSpeaking(false);
        _fireEnd("ended");
      };
      audio.onerror = () => {
        _pushDiag("serverTts:audio_error", {});
        _clearAudioRef();
        try { URL.revokeObjectURL(url); } catch { /* noop */ }
        // C-2 · render bridge. Fallback path (via runFallback →
        // speak()) will publish true again when browser TTS starts.
        _publishSpeaking(false);
        runFallback("audio_error");
      };
      // C-2 · render bridge: publish true when the server audio
      // actually begins playing. `isSpeaking()` already reports true
      // for a playing <audio>; this publish just guarantees the
      // React tree re-renders at that transition.
      audio.onplay = () => {
        _publishSpeaking(true);
        // PO 2026-08-30 · P0 Voice · D1 (late-arm barge-in). The
        // server-TTS route is now audibly playing through a path
        // Chromium's AEC can reference. This is the ONLY point at
        // which the barge-in detector may be armed. The browser
        // speechSynthesis fallback (runFallback → speak) never
        // reaches this branch and therefore never arms barge-in.
        _firePlaybackStart();
      };
      audio.onpause = () => {
        // Only publish false if the audio is not being cancelled
        // through cancelActiveSpeech (which handles its own publish)
        // AND the audio has actually stopped (not paused mid-play).
        try {
          if (_currentServerAudioCancelled) return;
          if (audio.ended) _publishSpeaking(false);
        } catch { /* noop */ }
      };
      try {
        // Stop any in-flight browser TTS before playing server audio.
        if (typeof window !== "undefined" && window.speechSynthesis) {
          try { window.speechSynthesis.cancel(); } catch { /* noop */ }
        }
        const p = audio.play();
        if (p && typeof p.catch === "function") {
          p.catch(() => runFallback("play_rejected"));
        }
        _pushDiag("serverTts:playing", { voice_id: opts.voice_id, locale });
      } catch (e) {
        runFallback("play_exception");
      }
    } catch (e) {
      runFallback(e && e.name === "AbortError" ? "timeout" : "fetch_error");
    }
  })();
  return true;
};

// ---------- Diagnostic snapshot (dev panel) ----------
export const diagSnapshot = () => {
  try {
    return {
      tts_enabled: isTtsEnabled(),
      pref_raw: readPrefRaw(),
      primed: _primed,
      voices_available: _voicesCache.length,
      speaking: !!window.speechSynthesis?.speaking,
      pending: !!window.speechSynthesis?.pending,
      paused: !!window.speechSynthesis?.paused,
      recent: getDiag(20),
    };
  } catch {
    return { error: "unavailable" };
  }
};

// Exported for unit tests only.
export const __internals = { pickEnglishVoice, pickSpanishVoice, EN_US_MALE_NAMES, ES_PREFERRED_NAMES, EN_EXCLUDE };
